import { Data, Grid as GridModule, Utils } from 'common';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import React from 'react';
import DateCellRenderer from './shared/DateCellRenderer.component';
import {
	carrierTrackingName,
	isCarrierTrackingSpecialCase,
} from './shared/DetailsDataHelper';
import { TrackingNumberType } from './shared/TrackingNumberType.enum';
import HistoryUtil from './utils/Util';

const { ServiceType } = Data;
const { GridPanel } = GridModule;
const { Session, Util } = Utils;

const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

class Grid extends React.Component {
	constructor(props) {
		super(props);

		const sessionData = Session.getData();
		let { statefulViews = '' } = sessionData.preferences;

		/* WEBAPPS-33306:  Commented unused code
		const { configuredCarriers = [] } = sessionData.accountInfo;
		const isEligibleForPayOnUse =
			Session.hasCapability(Capability.GpPayOnUse) ||
			Session.hasCapability(Capability.GpSmartSaverPayOnUse) ||
			Session.hasCapability(Capability.GapPayOnUse) ||
			Session.hasCapability(Capability.UspsReturn) ||
			Session.hasCapability(Capability.StandaloneUspsReturn);

		const hasUpsCoreEnabled = configuredCarriers.some(
			({ carrier, userOwnedAccount }) => carrier === Carrier.UPS && userOwnedAccount
		); */

		statefulViews =
			statefulViews && statefulViews !== ''
				? JSON.parse(decodeURIComponent(statefulViews))
				: {};

		// Temp fix for WEBAPPS-39108
		let savePrefs = false;

		if (statefulViews.hasOwnProperty('HistoryGridAgGrid')) {
			statefulViews.HistoryGridAgGrid.forEach(col => {
				if (col.colId === 'advanced_options_names') {
					if (col.sort !== null) {
						savePrefs = true;
						col.sort = null;
					}
				}
			});
		}

		if (savePrefs) {
			Session.savePreferences([
				{
					Name: 'statefulViews',
					Value: encodeURIComponent(JSON.stringify(statefulViews)),
				},
			]);
		}
		// End temp fix for WEBAPPS-39108

		this.statefulViews = statefulViews;

		this.state = {
			columnDefs: [
				{
					colId: 'created_date',
					headerName: 'Date Printed',
					minWidth: 120,
					field: 'transactionDate',
					headerCheckboxSelection: true,
					checkboxSelection: true,
					cellRenderer: 'DateCellRenderer',
					sort: 'desc',
				},
				{
					colId: 'total_amount',
					headerName: 'Amount Paid',
					minWidth: 95,
					field: 'totalAmount',
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'total_adjusted_amount',
					headerName: 'Adjusted Amount',
					minWidth: 110,
					valueGetter: ({ data: { printDetails } }) => {
						const { totalAdjustedAmount } = printDetails || {};
						return totalAdjustedAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
					cellClass: ({ value }) => (value < 0 ? 'negative-amount' : ''),
				},
				{
					colId: 'total_estimated_amount',
					headerName: 'Quoted Amount',
					minWidth: 95,
					valueGetter: ({ data: { printDetails } }) => {
						const { totalEstimatedAmount } = printDetails || {};
						return totalEstimatedAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'payment_type',
					headerName: 'Payment Type',
					minWidth: 95,
					sortable: true,
					valueGetter: ({ data: { printDetails } }) => {
						const { paymentTypeName = '' } = printDetails || {};
						return paymentTypeName;
					},
				},
				{
					colId: 'status',
					headerName: 'Shipment Status',
					minWidth: 130,
					valueGetter: ({ data: { printDetails } }) => {
						const { printStatusName = '' } = printDetails || {};
						return printStatusName;
					},
				},
				{
					colId: 'tracking_number',
					headerName: 'Tracking #',
					minWidth: 200,
					valueGetter: ({ data: { printDetails } }) => {
						const { trackingNumber, trackingNumberType } = printDetails || {};

						return HistoryUtil.showTracking(trackingNumber, trackingNumberType)
							? trackingNumber
							: '';
					},
					singleClickEdit: true,
					editable: ({ data: { printDetails } }) => {
						// TODO:
						// revisit here to figure out how to set the new tracking number in the DB
						const { trackingNumber, trackingNumberType } = printDetails || {};

						return HistoryUtil.showTracking(trackingNumber, trackingNumberType);
					},
				},
				{
					colId: 'delivered_date',
					headerName: 'Date Delivered',
					minWidth: 120,
					cellRenderer: 'DateCellRenderer',
					valueGetter: ({ data: { printDetails } }) => {
						const { trackingStatusDate, printStatus } = printDetails || {};

						return printStatus === 'delivered' ? trackingStatusDate : null;
					},
				},
				{
					colId: 'recipient',
					headerName: 'Recipient',
					minWidth: 120,
					preferredSibling: 'delivered_date',
					sortable: false,
					valueGetter: ({ data: { printDetails } }) =>
						Util.formatAddressToOneLine(printDetails.toAddress, true),
				},
				{
					colId: 'recipient_name',
					headerName: 'Name',
					minWidth: 120,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.name || '';
					},
				},
				{
					colId: 'address_line1',
					headerName: 'Address 1',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.addressLine1 || '';
					},
				},
				{
					colId: 'address_line2',
					headerName: 'Address 2',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.addressLine2 || '';
					},
				},
				{
					colId: 'address_line3',
					headerName: 'Address 3',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.addressLine3 || '';
					},
				},
				{
					colId: 'city',
					headerName: 'City',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.city || '';
					},
				},
				{
					colId: 'state_province',
					headerName: 'State/Province',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.stateProvince || '';
					},
				},
				{
					colId: 'to_address_postal_code',
					headerName: 'Postal Code',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.postalCode || '';
					},
				},
				{
					colId: 'country_code',
					headerName: 'Country',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { toAddress = {} } = printDetails || {};
						return toAddress.countryCode || '';
					},
				},
				{
					colId: 'postal_code',
					headerName: 'Origin Zip',
					minWidth: 90,
					sortable: true,
					valueGetter: ({ data: { printDetails } }) => {
						const { fromAddress = {}, printTypeName = '' } = printDetails || {};
						return printTypeName === 'NetStamps' ? '' : fromAddress.postalCode;
					},
				},
				{
					colId: 'weight_pounds',
					headerName: 'Weight',
					minWidth: 90,
					sortable: true,
					valueGetter: ({ data }) => {
						const {
							weightPounds = 0,
							weightOunces = 0,
							serviceType,
							printTypeId,
						} = data.printDetails || {};
						if (
							ServiceType.UspsFirstClassMailIntl === serviceType &&
							printTypeId === 5 &&
							weightPounds === 0 &&
							weightOunces === 0
						) {
							return '';
						}

						return `${weightPounds}lb ${weightOunces}oz`;
					},
				},
				{
					colId: 'carrier',
					headerName: 'Carrier',
					minWidth: 80,
					valueGetter: ({ data }) => {
						const { carrierName = '' } = data.printDetails || {};

						return carrierName;
					},
				},
				{
					colId: 'service_type',
					headerName: 'Service',
					minWidth: 120,
					valueGetter: ({ data: { printDetails } }) => {
						const {
							serviceTypeName = '',
							isReturnLabel = false,
							carrier = '',
						} = printDetails || {};

						return HistoryUtil.formattedServiceTypeName(
							isReturnLabel,
							carrier,
							serviceTypeName
						);
					},
				},
				{
					colId: 'tracking_confirmation',
					headerName: 'Tracking Confirmation',
					preferredSibling: 'service_type',
					minWidth: 200,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const {
							deliveryConfirmationTypeName = '',
							deliveryConfirmationAmount = 0,
							carrier = '',
							carrierName = '',
							trackingNumber,
							trackingNumberType,
						} = printDetails || {};

						const displayTrackingName = isCarrierTrackingSpecialCase({
							deliveryConfirmationAmount,
							carrier,
						})
							? carrierTrackingName({
									carrier,
									carrierName,
									deliveryConfirmationAmount,
								})
							: deliveryConfirmationTypeName;

						return `${
							HistoryUtil.showTracking(trackingNumber, trackingNumberType)
								? displayTrackingName
								: ''
						}`;
					},
				},
				{
					colId: 'advanced_options_names',
					headerName: 'Extra Services',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { advancedOptionsNames = '' } = printDetails || {};
						return advancedOptionsNames;
					},
				},
				{
					colId: 'insured_value',
					headerName: 'Insured For',
					minWidth: 95,
					sortable: true,
					valueGetter: ({ data: { printDetails } }) => {
						const { insuredValue } = printDetails || {};
						return insuredValue;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'ship_date',
					headerName: 'Ship Date',
					minWidth: 120,
					valueGetter: ({ data: { printDetails } }) => {
						const { shipDate = '', printTypeName = '' } = printDetails || {};
						return printTypeName === 'NetStamps' ? '' : shipDate;
					},
					cellRenderer: 'DateCellRenderer',
				},
				{
					colId: 'cost_code',
					headerName: 'Cost Code',
					minWidth: 120,
					valueGetter: ({ data: { printDetails } }) => {
						const { costCodeName = '' } = printDetails || {};
						return costCodeName;
					},
				},
				{
					colId: 'memo',
					headerName: 'Printed Message',
					minWidth: 150,
					valueGetter: ({ data: { printDetails } }) => {
						const { memo = '' } = printDetails || {};
						return memo;
					},
				},
				{
					colId: 'username',
					headerName: 'User',
					minWidth: 120,
					field: 'username',
					sortable: true,
				},
				{
					colId: 'refund_type',
					headerName: 'Refund Type',
					minWidth: 110,
					field: 'refundType',
					valueGetter: ({ data: { printDetails } }) => {
						const {
							refundType = '',
							advancedOptionsNames,
							isRefundAssist,
							refundStatus,
						} = printDetails || {};

						// TODO: Remove this after server fix - this is a temporary fix for WEBAPPS-38422
						if (advancedOptionsNames.includes('Certified Mail')) {
							return 'E-refund';
						}

						if (isRefundAssist || refundStatus === 'Request Scheduled') {
							return 'Refund Assist';
						}

						return refundType;
					},
				},
				{
					colId: 'refund_requested_date',
					headerName: 'Refund Request Date',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { refundRequestedDate = '' } = printDetails || {};
						const date = new Date(refundRequestedDate);

						// check if date is valid after converting the refundRequestedDate string to Date object
						return date.getTime() ? date : '';
					},
					cellRenderer: 'DateCellRenderer',
				},
				{
					colId: 'refund_status',
					headerName: 'Refund Status',
					minWidth: 110,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { refundStatus = '' } = printDetails || {};
						return refundStatus;
					},
				},
				{
					colId: 'refund_requested_amount',
					headerName: 'Refund Requested',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { refundRequestedAmount = '' } = printDetails || {};
						return refundRequestedAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'reference_field1',
					headerName: 'Reference 1',
					minWidth: 140,
					sortable: true,
					valueGetter: ({ data: { printDetails } }) => {
						const { referenceField1 = '' } = printDetails || {};
						return referenceField1;
					},
				},
				{
					colId: 'reference_field2',
					headerName: 'Reference 2',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { referenceField2 = '' } = printDetails || {};
						return referenceField2;
					},
				},
				{
					colId: 'reference_field3',
					headerName: 'Reference 3',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { referenceField3 = '' } = printDetails || {};
						return referenceField3;
					},
				},
				{
					colId: 'reference_field4',
					headerName: 'Reference 4',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { referenceField4 = '' } = printDetails || {};
						return referenceField4;
					},
				},
				{
					colId: 'order_id',
					headerName: 'Order ID',
					minWidth: 140,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const { orderId = '' } = printDetails || {};
						return orderId;
					},
				},
				{
					colId: 'order_source',
					headerName: 'Store',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderSource = '' } = orderDetails || {};
						return orderSource;
					},
				},
				{
					colId: 'order_date',
					headerName: 'Order Date',
					minWidth: 120,
					cellRenderer: 'DateCellRenderer',
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderDate = '' } = orderDetails || {};
						return orderDate;
					},
				},
				{
					colId: 'order_total_amount',
					headerName: 'Order Total',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderTotalAmount = '' } = orderDetails || {};
						return orderTotalAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'item_skus',
					headerName: 'Item SKUs',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { itemSkus = '' } = orderDetails || {};
						return itemSkus;
					},
				},
				{
					colId: 'item_quantities_and_names',
					headerName: 'Items',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { itemQuantitiesAndNames = '' } = orderDetails || {};
						return itemQuantitiesAndNames;
					},
				},
				{
					colId: 'order_subtotal_amount',
					headerName: 'Product Total',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderSubtotalAmount = '' } = orderDetails || {};
						return orderSubtotalAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'order_shipping_and_handling_amount',
					headerName: 'Shipping Paid',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderShippingAndHandlingAmount = '' } = orderDetails || {};
						return orderShippingAndHandlingAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'order_tax_amount',
					headerName: 'Tax Paid',
					minWidth: 120,
					sortable: false,
					valueGetter: ({ data: { orderDetails } }) => {
						const { orderTaxAmount = '' } = orderDetails || {};
						return orderTaxAmount;
					},
					cellRenderer: ({ value }) =>
						isNumber(value) ? usCurrencyFormatter.format(value) : '',
				},
				{
					colId: 'insurance_type_name',
					headerName: 'Insurance Provider',
					minWidth: 115,
					sortable: true,
					valueGetter: ({ data: { printDetails } }) => {
						const { insuranceTypeName = '' } = printDetails || {};
						return insuranceTypeName;
					},
				},
				{
					colId: 'duties_and_taxes',
					headerName: 'Duties and Taxes Amount',
					minWidth: 115,
					sortable: false,
					valueGetter: ({ data: { printDetails } }) => {
						const {
							advancedOptions: { deliveredDutyPaidAmount = '' },
						} = printDetails || {};
						return deliveredDutyPaidAmount;
					},
				},
			],
		};
	}

	buildAddress = ({ data }) => {
		let freeFormAddress = [
			data.destinationFullName,
			data.destinationTitle,
			data.destinationDepartment,
			data.destinationCompany,
			data.destinationAddress1,
			data.destinationAddress2,
			data.destinationAddress3,
			data.destinationCity,
			data.destinationState,
		]
			.filter(addressPart => addressPart)
			.join(', ');

		// append zip code and zip add-on
		if (data.destinationZip) {
			freeFormAddress += ` ${data.destinationZip}`;

			if (data.destinationZipAddOn) {
				freeFormAddress += `-${data.destinationZipAddOn}`;
			}
		}

		// return free-from address built from itemized fields otherwise recipientAddress
		return freeFormAddress !== '' ? freeFormAddress : data.recipientAddress;
	};

	onColumnStateChanged = columnState => {
		this.statefulViews = {
			...this.statefulViews,
			HistoryGridAgGrid: columnState,
		};

		// If all columns are hidden, don't save the current column state to the session preferences.
		if (!columnState.every(col => col.hide)) {
			Session.savePreferences([
				{
					Name: 'statefulViews',
					Value: encodeURIComponent(JSON.stringify(this.statefulViews)),
				},
			]);
		}
	};

	render() {
		const {
			prints,
			pageSize,
			fetchPrints,
			onSortChanged,
			onSelectionChanged,
			onGridReady,
			nextPageRequest,
			isNextPageRequest,
			onRowDataUpdated,
			suppressLoadingOverlay,
		} = this.props;

		const { columnDefs } = this.state;

		const columnState = isEmpty(this.statefulViews.HistoryGridAgGrid)
			? []
			: this.statefulViews.HistoryGridAgGrid;

		// Adds any column not currently added to statefulViews at preferredSibling if available
		if (columnState.length > 0 && columnState.length !== columnDefs.length) {
			const columnStateIds = columnState.map(col => col.colId);
			const columnDefIds = columnDefs.map(col => col.colId);

			columnDefs.map(col => {
				if (columnStateIds.indexOf(col.colId) === -1) {
					const insertAtIndex = col.hasOwnProperty('preferredSibling')
						? columnDefIds.indexOf(col.preferredSibling) + 1
						: columnState.length;

					columnState.splice(insertAtIndex, 0, { colId: col.colId });
				}
				return col;
			});
		}

		return (
			<GridPanel
				className="history history-grid"
				rowData={prints}
				columnDefs={columnDefs}
				showEndOfDataMessage={false}
				pageSize={pageSize}
				onColumnStateChanged={this.onColumnStateChanged}
				columnState={columnState}
				onGridReady={onGridReady}
				onSelectionChanged={onSelectionChanged}
				onSortChanged={onSortChanged}
				defaultColDef={{ suppressSizeToFit: true }}
				fetchData={fetchPrints}
				frameworkComponents={{ DateCellRenderer }}
				dataqa="history-grid"
				infiniteScroll
				nextPageRequest={nextPageRequest}
				nextPage={isNextPageRequest}
				onRowDataUpdated={onRowDataUpdated}
				noRowsOverlayParams={{
					noDataOverlayText: true,
				}}
				suppressLoadingOverlay={suppressLoadingOverlay}
				suppressPropertyNamesCheck
			/>
		);
	}
}

Grid.propTypes = {
	prints: PropTypes.array,
	onSortChanged: PropTypes.func.isRequired,
	onSelectionChanged: PropTypes.func.isRequired,
	onGridReady: PropTypes.func.isRequired,
	pageSize: PropTypes.number,
};

export default Grid;
